import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  SHEET_FRAME_DECISION_CONFLICT,
  fetchSheetFrameProposals,
  saveSheetFrameDecision,
} from '../../services/drawingGeometryApi';
import {
  BAND_DISPLAYABLE,
  BAND_ROTATED_PAGE,
  bandDisplayability,
} from '../../utils/sheetFrameBbox.js';

/**
 * Sheet frame proposals — per-sheet, per-page review of detected border bands.
 *
 * The detector proposes strips of a drawing sheet that LOOK like title-block or
 * border regions. It cannot decide what gets removed, and in this slice nothing
 * it proposes is removed at all: a decision recorded here is an audit record for
 * a later, separately-approved slice to act on. Every presentation choice below
 * follows from that, and from Slice 2 design review §5.1's six rules:
 *
 *   · NOTHING IS PRE-SELECTED. `MappingCheckPanel` auto-previews its first queue
 *     item; that pattern is deliberately not copied, because auto-focus reads as
 *     a recommendation and the detector has no standing to recommend.
 *   · RANK 0 GETS NO EMPHASIS. `d3Rank` is an ordering, not a ranking of
 *     correctness. Rows sort by it; nothing about rank 0 renders differently.
 *   · CONFIRM AND REJECT ARE THE SAME CONTROL. One styled component, one
 *     variant. Giving Confirm the primary treatment would make rejection read as
 *     the deviation from an expected answer.
 *   · REVERSAL IS A FLIP, NEVER A DELETE. The MVP stores `valid ↔ rejected` as a
 *     conditional full-item put, so "Change to Rejected" is literally what
 *     happens; "Undo" or "Delete" would describe an operation that does not
 *     exist and that the execution role cannot perform.
 */

// ---------------------------------------------------------------------------
// Vocabulary — mirrors the frozen server-side enums. Kept as flat maps rather
// than logic so an unknown code falls through to a safe default instead of
// throwing at a reviewer.
// ---------------------------------------------------------------------------

const REASON_PROVENANCE_CHANGED = 'resolver_provenance_changed';
const REASON_PROPOSAL_CHANGED = 'resolver_proposal_changed';
const REASON_CONFIRMED_PROPOSAL_ABSENT = 'resolver_confirmed_proposal_absent';
const REASON_CONFLICTING_DECISIONS = 'resolver_conflicting_decisions';
const REASON_AMBIGUOUS_TARGET = 'resolver_ambiguous_target';
const REASON_TEMPLATE_INERT = 'resolver_template_inert';

export const STATE_NOT_REVIEWED = 'not_reviewed';
export const STATE_CONFIRMED = 'confirmed';
export const STATE_REJECTED = 'rejected';
export const STATE_NEEDS_REREVIEW = 'needs_rereview';

const STATE_LABELS = {
  [STATE_NOT_REVIEWED]: 'Not reviewed',
  [STATE_CONFIRMED]: 'Confirmed',
  [STATE_REJECTED]: 'Rejected',
  [STATE_NEEDS_REREVIEW]: 'Needs re-review',
};

/** §5.3's user-facing presentation of each resolver reason code. */
const RESOLUTION_NOTES = {
  [REASON_PROVENANCE_CHANGED]:
    'Re-extracted with different tooling — your earlier decision no longer applies to a verified band.',
  [REASON_PROPOSAL_CHANGED]: 'The detected band moved. Review again.',
  // These two are NOT the same thing and must not read as though they are.
  // `ambiguous_target` is ordinary reviewer behaviour with a one-click remedy;
  // `conflicting_decisions` is a stored-data fault the reviewer cannot fix and
  // must not be invited to try. Rendering both as "Conflicting decisions" told
  // a reviewer who had simply picked two bands that something was broken, and
  // would have made a genuine duplicate-writer defect indistinguishable from
  // that everyday case.
  [REASON_CONFLICTING_DECISIONS]:
    'This band has conflicting stored decisions, so nothing is authorized. It cannot be resolved here.',
  [REASON_AMBIGUOUS_TARGET]:
    'More than one band is confirmed on this edge, so nothing is authorized. Reject all but one.',
  // `resolver_no_match` is deliberately absent: §5.3 renders it as a plain
  // "Not reviewed" with no explanatory note, because there is nothing to
  // explain — no decision has ever been recorded against this band.
  // `resolver_template_inert` is deliberately absent too; see below.
};

/**
 * Why an otherwise-valid request has nothing to show.
 *
 * Every one of these arrives as a 200 with an empty proposal list. They are
 * ordinary states of this endpoint, not errors, and none of them is retryable
 * by the reviewer — so none of them is presented as a failure.
 */
const UNAVAILABLE_NOTES = {
  no_sidecar:
    'Sheet frame detection has not run for this drawing. This is the normal state while detection is switched off.',
  no_drawing_ir: 'This sheet has no extracted drawing data yet.',
  ambiguous_document:
    'More than one version of this drawing is stored for this sheet, and nothing here picks one for you. Re-extract the sheet, or ask for the version you want to review.',
  page_not_analyzed: 'This page was not part of the analysed set.',
  no_frame_detected: 'No sheet frame was detected on this page.',
  sidecar_not_current:
    'The stored analysis was produced by a different version of the detector and is no longer trusted.',
  sidecar_unreadable: 'The stored analysis for this sheet could not be read.',
  sidecar_describes_another_target: 'The stored analysis does not describe this sheet.',
  page_index_disagrees_with_evidence: 'The stored analysis disagrees with itself about this page.',
  no_document_digest: 'The stored analysis is missing the document identifier decisions bind to.',
};

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const PanelHeader = styled.div`
  padding: 20px 20px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #333;
`;

/**
 * The §5.1 rule-6 banner. Persistent, not dismissible, and rendered before any
 * proposal so it cannot be scrolled past unread. In this slice it is literally
 * true, and saying so plainly is the honest framing.
 */
const Banner = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 12px;
  line-height: 1.5;
  color: #4b5563;
`;

const Body = styled.div`
  padding: 16px 20px 24px;
`;

const Note = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
`;

const ErrorNote = styled(Note)`
  color: #b91c1c;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
`;

/**
 * One proposal row.
 *
 * `$selected` is the ONLY thing that changes a row's border or background.
 * Detector order deliberately has no effect here — see the rank-0 rule above.
 */
const Row = styled.li`
  border: 1px solid ${(p) => (p.$selected ? '#e97024' : 'rgba(0,0,0,0.12)')};
  border-radius: 10px;
  background: ${(p) => (p.$selected ? 'rgba(233,112,36,0.06)' : '#fff')};
  padding: 12px;
`;

const RowButton = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const EdgeLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #333;
  text-transform: capitalize;
`;

const StateChip = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  /* Neutral by default. The default state is never green: an unreviewed band
     must not read as an approved one. */
  background: ${(p) => (p.$state === STATE_CONFIRMED ? '#d1fae5'
    : p.$state === STATE_REJECTED ? '#fee2e2'
      : p.$state === STATE_NEEDS_REREVIEW ? '#fef3c7' : '#f3f4f6')};
  color: ${(p) => (p.$state === STATE_CONFIRMED ? '#047857'
    : p.$state === STATE_REJECTED ? '#b91c1c'
      : p.$state === STATE_NEEDS_REREVIEW ? '#92400e' : '#6b7280')};
`;

const Facts = styled.dl`
  margin: 10px 0 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  font-size: 12px;
  color: #6b7280;

  dt { font-weight: 600; }
  dd { margin: 0; }
`;

const OrderCaption = styled.p`
  margin: 8px 0 0;
  font-size: 11px;
  line-height: 1.45;
  color: #6b7280;
  font-style: italic;
`;

const ResolutionNote = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #92400e;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

/**
 * The decision control. ONE component with NO emphasis variant, used for both
 * Confirm and Reject, so the two can never drift apart visually.
 */
const DecisionBtn = styled.button`
  flex: 1;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const Section = styled.section`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: #444;
`;

const DiagnosticList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 8px;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;
`;

const ConflictNote = styled.div`
  margin: 10px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  font-size: 12px;
  line-height: 1.5;
  color: #92400e;
`;

// ---------------------------------------------------------------------------
// Derivations
// ---------------------------------------------------------------------------

/**
 * The single explicit state every proposal carries.
 *
 * A drift reason WINS over a stored verdict, and that ordering is the point: a
 * band whose provenance or geometry changed has a stored decision that no
 * longer applies to it, so showing "Confirmed" would assert an authorization
 * the resolver has already withdrawn.
 */
export function proposalState(proposal) {
  const reasonCode = proposal?.resolution?.reasonCode;
  if (reasonCode === REASON_PROVENANCE_CHANGED || reasonCode === REASON_PROPOSAL_CHANGED) {
    return STATE_NEEDS_REREVIEW;
  }
  const verdict = proposal?.decision?.verdict;
  if (verdict === 'valid') return STATE_CONFIRMED;
  if (verdict === 'rejected') return STATE_REJECTED;
  return STATE_NOT_REVIEWED;
}

/**
 * Is this band un-decidable right now?
 *
 * Blocked ONLY when the resolver cannot say which band a decision refers to,
 * because accepting either verdict would then record an authorization against
 * an unidentified target. That is `resolver_conflicting_decisions`: two stored
 * decisions matched ONE authorization fingerprint, so the store has more than
 * one writer for a single band and nothing entered here can say which was meant.
 *
 * **`resolver_ambiguous_target` is deliberately NOT blocked**, even though it
 * also reports `state: 'ambiguous'`. Its target is not unidentified at all --
 * every proposal carries its own unique authorization fingerprint and the
 * resolver knows exactly which band each decision refers to. It withholds
 * authorization because two confirmed bands would carve the SAME EDGE, which is
 * an ambiguity BETWEEN bands rather than about any one of them.
 *
 * Blocking it was a deadlock with no exit. A reviewer confirms two bands on one
 * edge -- ordinary behaviour, and the only way to compare them -- both go
 * ambiguous, and the controls that could clear the ambiguity are the controls
 * that just got disabled. The guard prevented the sole remedy for the state it
 * was reporting.
 *
 * Re-deciding is safe here by the resolver's own contract: fewer authorized
 * bands can only ever preserve MORE content, so backing a confirmation out can
 * never widen what would be removed.
 */
export function isDecisionBlocked(proposal) {
  const resolution = proposal?.resolution;
  if (!resolution) return true;
  return resolution.reasonCode === REASON_CONFLICTING_DECISIONS;
}

/**
 * Why this band cannot be reviewed, because it cannot be SHOWN.
 *
 * The controls here authorize the eventual removal of drawing content, and a
 * reviewer confirming a band is asserting they looked at the region it covers.
 * If the overlay cannot place that region on the sheet, the assertion has no
 * basis — so the decision is refused rather than accepted on trust.
 *
 * The reachable case is a rotated page: the engine pre-applies /Rotate to the
 * coordinates it stores while pdf.js applies rotation inside its own transform,
 * so the overlay fails closed there (`sheetFrameBandToCanvas`) and the frozen
 * corpus contains a 270° sheet. Before this gate existed the band silently
 * vanished from the drawing while Confirm and Reject stayed live.
 */
export function undisplayableReason(proposal, pageEvidence) {
  return bandDisplayability(proposal?.bbox, pageEvidence);
}

const UNDISPLAYABLE_NOTES = {
  [BAND_ROTATED_PAGE]:
    'This page is rotated, and the band cannot yet be drawn on it. Nothing can be confirmed or rejected here until it can be shown.',
};

const UNDISPLAYABLE_FALLBACK =
  'This band could not be placed on the drawing, so there is nothing to review against. Nothing can be confirmed or rejected here.';

/**
 * "Detector order: N of M", where M counts the proposals on the SAME EDGE.
 *
 * `d3Rank` is a candidate's position among its own edge's ranked candidates, so
 * a per-edge denominator is the only one that makes the numerator mean
 * anything. `d4Index` is a different, global ordering and is displayed as its
 * own separately-labelled fact — never derived from `d3Rank`, matching the
 * detector contract.
 */
export function detectorOrderLabel(proposal, proposals) {
  const rank = proposal?.d3Rank;
  if (!Number.isInteger(rank) || rank < 0) return null;
  const sameEdge = proposals.filter((p) => p.edge === proposal.edge).length;
  return `Detector order: ${rank + 1} of ${sameEdge}`;
}

function shortId(value) {
  const text = String(value || '');
  return text.length > 12 ? `${text.slice(0, 12)}…` : text;
}

function formatPt(value) {
  return Number.isFinite(value) ? `${Math.round(value * 10) / 10} pt` : '—';
}

// ---------------------------------------------------------------------------

/**
 * @param {object}   props
 * @param {string}   props.projectId
 * @param {string}   props.requirementId
 * @param {string}   props.sheetId
 * @param {number}   props.pageIndex zero-based; `null` renders an explicit
 *   "no page" state and fetches nothing, rather than defaulting to page 0 and
 *   binding a decision to a page the reviewer is not looking at.
 * @param {Function} props.onSelectionChange receives
 *   `{ proposal, proposals, pageEvidence } | null` so a parent can drive the
 *   viewer overlay. Selection is owned here; the callback only mirrors it
 *   outward. `pageEvidence` travels WITH the selection so the viewer can only
 *   ever transform a band using the evidence of the page it came from.
 */
export default function SheetFrameProposalsPanel({
  projectId,
  requirementId,
  sheetId,
  pageIndex = null,
  onSelectionChange,
}) {
  const [payloadEntry, setPayloadEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFingerprint, setSelectedFingerprint] = useState(null);
  const [pendingFingerprint, setPendingFingerprint] = useState(null);
  const [conflict, setConflict] = useState(null);

  // Monotonic request id. Every fetch captures the value it started under and
  // discards its own result if a newer one has begun -- the sheet and page can
  // both change while a request is in flight, and an out-of-order response
  // would otherwise repaint the panel with another page's proposals while the
  // header still names this one.
  const requestSeq = useRef(0);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const ready = Boolean(projectId && requirementId && sheetId)
    && Number.isInteger(pageIndex) && pageIndex >= 0;

  /**
   * Exactly which page a payload belongs to.
   *
   * `JSON.stringify` of the tuple rather than a delimiter-joined string:
   * these identifiers are free-form, and any separator picked for being
   * "unlikely" is a collision waiting for the sheet id that contains it.
   * Quoting each component makes the encoding unambiguous for every input.
   */
  const targetKey = JSON.stringify([projectId, requirementId, sheetId, pageIndex]);
  const targetKeyRef = useRef(targetKey);
  targetKeyRef.current = targetKey;

  const load = useCallback(() => {
    if (!ready) {
      setPayloadEntry(null);
      setError(null);
      return;
    }
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    const key = targetKeyRef.current;
    setLoading(true);
    setError(null);

    fetchSheetFrameProposals(projectId, requirementId, sheetId, pageIndex)
      .then((data) => {
        if (requestSeq.current !== seq) return;
        // Stored WITH the page it describes, never bare -- see `payload` below.
        setPayloadEntry({ key, data });
      })
      .catch(() => {
        if (requestSeq.current !== seq) return;
        setPayloadEntry(null);
        // No identifiers, no response body, no error text from the wire.
        setError('Could not load sheet frame proposals for this page.');
      })
      .finally(() => {
        if (requestSeq.current !== seq) return;
        setLoading(false);
      });
  }, [ready, projectId, requirementId, sheetId, pageIndex]);

  /**
   * The payload, but ONLY if it belongs to the page currently selected.
   *
   * Derived during render rather than cleared in an effect. Clearing in an
   * effect leaves one committed render in which the new page's props are paired
   * with the previous page's proposals -- long enough for those rows to be
   * visible and clickable, and (when the two pages happen to be the same size,
   * which defeats the overlay's page-size cross-check) long enough for the
   * viewer to draw the old bands over the new sheet. Keying the state makes
   * that pairing unrepresentable instead of merely brief.
   */
  const payload = payloadEntry && payloadEntry.key === targetKey
    ? payloadEntry.data
    : null;

  // Selection resets whenever the target changes, and is never restored from a
  // previous sheet or page: a fingerprint is only meaningful against the page
  // that produced it.
  useEffect(() => {
    // Belt and braces, and knowingly so. `load` bumps the sequence itself
    // whenever it starts a request, which already covers every target change
    // that starts a new one. This bump only additionally covers a response
    // in flight when the panel goes NOT-ready -- and while not-ready the panel
    // returns its "no page" branch without reading `payload` at all, so the
    // difference is not observable today. It is kept so that the guard does not
    // quietly depend on that early return staying where it is.
    requestSeq.current += 1;
    setSelectedFingerprint(null);
    setConflict(null);
    setPendingFingerprint(null);
    onSelectionChangeRef.current?.(null);
  }, [projectId, requirementId, sheetId, pageIndex]);

  useEffect(() => { load(); }, [load]);

  const proposals = useMemo(
    () => (Array.isArray(payload?.dividerProposals) ? payload.dividerProposals : []),
    [payload]
  );

  const sortedProposals = useMemo(() => {
    // Sorting BY detector order is fine; highlighting it is not (§5.1 rule 4).
    // Edge first so a reviewer reads one side of the sheet at a time.
    const copy = [...proposals];
    copy.sort((a, b) => {
      if (a.edge !== b.edge) return String(a.edge).localeCompare(String(b.edge));
      return (a.d3Rank ?? 0) - (b.d3Rank ?? 0);
    });
    return copy;
  }, [proposals]);

  const diagnostics = useMemo(
    () => (Array.isArray(payload?.decisionDiagnostics) ? payload.decisionDiagnostics : []),
    [payload]
  );

  const absentDecisions = useMemo(
    () => diagnostics.filter((d) => d.reasonCode === REASON_CONFIRMED_PROPOSAL_ABSENT),
    [diagnostics]
  );

  const otherDiagnostics = useMemo(
    () => diagnostics.filter((d) => (
      d.reasonCode !== REASON_CONFIRMED_PROPOSAL_ABSENT
      // Not surfaced: no template-scoped decision can exist in the MVP, so
      // showing this would describe a state the product cannot reach.
      && d.reasonCode !== REASON_TEMPLATE_INERT
    )),
    [diagnostics]
  );

  const handleSelect = useCallback((proposal) => {
    const fingerprint = proposal?.authorizationFingerprint || null;
    setConflict(null);
    setSelectedFingerprint((current) => {
      const next = current === fingerprint ? null : fingerprint;
      onSelectionChangeRef.current?.(
        next === null ? null : {
          proposal,
          proposals,
          pageEvidence: payload?.pageEvidence || null,
        }
      );
      return next;
    });
  }, [payload, proposals]);

  const handleDecide = useCallback((proposal, verdict) => {
    const fingerprint = proposal?.authorizationFingerprint;
    if (!fingerprint || !payload) return;
    // The same two conditions the controls are rendered under, restated where
    // the write actually happens: a decision requires that this exact band is
    // SELECTED and that it can be DRAWN. Together they are the product promise
    // -- a reviewer confirming a band is asserting they looked at the region it
    // covers, and both halves have to hold for that assertion to mean anything.
    //
    // Unreachable through the rendered UI, and deliberately kept. The controls
    // do not exist unless both conditions hold, so nothing can click through to
    // here; deleting these lines breaks no test, which is a fact about
    // reachability rather than about coverage. They are what stops a future
    // caller of this handler -- a keyboard shortcut, a bulk action, a test
    // harness -- from quietly reintroducing S2C-003.
    if (proposal.authorizationFingerprint !== selectedFingerprint) return;
    if (undisplayableReason(proposal, payload.pageEvidence) !== BAND_DISPLAYABLE) return;

    const stored = proposal.decision;
    const isFlip = Boolean(stored && stored.decisionId);
    if (isFlip && !Number.isInteger(stored.revision)) {
      // A stored decision the server did not give a usable revision for cannot
      // be flipped safely: the conditional write needs both halves, and
      // inventing one would be exactly the lost-update the design forbids.
      setError('This decision cannot be changed right now. Reload the page and try again.');
      return;
    }

    setPendingFingerprint(fingerprint);
    setConflict(null);
    setError(null);

    // The page this write belongs to, captured before it is sent. Everything in
    // the handlers below is scoped to it.
    const writeKey = targetKeyRef.current;

    saveSheetFrameDecision({
      projectId,
      requirementId,
      sheetId,
      pageIndex,
      // Echoed exactly as the server issued them.
      documentDigest: payload.documentDigest,
      authorizationFingerprint: fingerprint,
      verdict,
      // First decision: revision 0 and NO decision id. Flip: both halves.
      expectedRevision: isFlip ? stored.revision : 0,
      expectedDecisionId: isFlip ? stored.decisionId : null,
    })
      .then((result) => {
        // The reviewer may have moved to another page while this was in flight.
        // `load` is captured from the render that started the write, so calling
        // it unconditionally would re-fetch THAT page and -- because it takes a
        // fresh, newest sequence number -- install the old page's proposals as
        // the current ones. The conflict banner would be misfiled the same way.
        if (targetKeyRef.current !== writeKey) return;
        if (result.outcome === SHEET_FRAME_DECISION_CONFLICT) {
          // Never a silent overwrite and never an automatic retry: show what
          // the server currently holds, then reload so every row is consistent
          // with it.
          setConflict({ fingerprint, current: result.current });
        }
        load();
      })
      .catch(() => {
        if (targetKeyRef.current !== writeKey) return;
        setError('That decision could not be saved. Reload the page and try again.');
      })
      .finally(() => {
        if (targetKeyRef.current !== writeKey) return;
        setPendingFingerprint((current) => (current === fingerprint ? null : current));
      });
  }, [payload, projectId, requirementId, sheetId, pageIndex, load, selectedFingerprint]);

  const header = (
    <PanelHeader>
      <PanelTitle>Sheet frame proposals</PanelTitle>
      <Banner>
        Nothing here changes extraction. Confirming records a decision for later review.
      </Banner>
    </PanelHeader>
  );

  if (!ready) {
    return (
      <PanelRoot>
        {header}
        <Body>
          <Note>Open a drawing page to review its sheet frame proposals.</Note>
        </Body>
      </PanelRoot>
    );
  }

  return (
    <PanelRoot>
      {header}
      <Body>
        {error && <ErrorNote role="alert">{error}</ErrorNote>}

        {loading && !payload && <Note>Loading sheet frame proposals…</Note>}

        {payload && payload.available === false && (
          <Note data-testid="sheet-frame-unavailable" data-reason={payload.unavailableReason}>
            {UNAVAILABLE_NOTES[payload.unavailableReason]
              || 'There is nothing to review on this page right now.'}
          </Note>
        )}

        {payload && payload.available === true && sortedProposals.length === 0 && (
          <Note>No border bands were proposed on this page.</Note>
        )}

        {payload && payload.available === true && sortedProposals.length > 0
          && selectedFingerprint === null && (
          // The workflow the panel wants read left to right: select, look at the
          // band on the drawing, then decide. Saying so is cheap, and without it
          // a list whose rows carry no controls reads as a list you cannot act
          // on rather than one you have not opened yet.
          <Note data-testid="sheet-frame-select-hint">
            Select a proposal to review its band.
          </Note>
        )}

        {payload && payload.available === true && sortedProposals.length > 0 && (
          <List>
            {sortedProposals.map((proposal) => {
              const fingerprint = proposal.authorizationFingerprint;
              const state = proposalState(proposal);
              const selected = fingerprint != null && fingerprint === selectedFingerprint;
              const undisplayable = undisplayableReason(proposal, payload.pageEvidence);
              // A decision exists only while its band is selected AND drawable.
              // Both halves are RENDER conditions, not `disabled` conditions:
              // a control that is not on the page cannot be pressed, whereas a
              // disabled one is a styling attribute that a refactor can drop
              // without any test noticing.
              const decidable = selected && undisplayable === BAND_DISPLAYABLE;
              // The resolver's own blocks stay a `disabled` state rather than a
              // render condition, because they mean something different: the
              // band IS being shown and the reviewer IS looking at it, and the
              // controls are present-but-refused with a reason next to them.
              const blocked = isDecisionBlocked(proposal);
              const busy = pendingFingerprint === fingerprint;
              const orderLabel = detectorOrderLabel(proposal, proposals);
              const note = RESOLUTION_NOTES[proposal?.resolution?.reasonCode];
              const confirmed = state === STATE_CONFIRMED;
              const rejected = state === STATE_REJECTED;

              return (
                <Row key={fingerprint} $selected={selected} data-testid="sheet-frame-proposal">
                  <RowButton
                    type="button"
                    aria-pressed={selected}
                    onClick={() => handleSelect(proposal)}
                  >
                    <RowTop>
                      <EdgeLabel>{proposal.edge} band</EdgeLabel>
                      <StateChip $state={state} data-testid="sheet-frame-state">
                        {STATE_LABELS[state]}
                      </StateChip>
                    </RowTop>
                    <Facts>
                      {orderLabel && (
                        <>
                          <dt>Order</dt>
                          <dd data-testid="sheet-frame-order">{orderLabel}</dd>
                        </>
                      )}
                      <dt>Alternative index</dt>
                      <dd>
                        {Number.isInteger(proposal.d4Index)
                          ? proposal.d4Index
                          : 'Not a retained alternative'}
                      </dd>
                      <dt>Depth</dt>
                      <dd>{formatPt(proposal.distancePt)}</dd>
                    </Facts>
                    <OrderCaption>
                      Order only — the detector cannot decide what is removed.
                    </OrderCaption>
                  </RowButton>

                  {note && <ResolutionNote>{note}</ResolutionNote>}

                  {undisplayable !== BAND_DISPLAYABLE && (
                    <ResolutionNote
                      data-testid="sheet-frame-undisplayable"
                      data-reason={undisplayable}
                    >
                      {UNDISPLAYABLE_NOTES[undisplayable] || UNDISPLAYABLE_FALLBACK}
                    </ResolutionNote>
                  )}

                  {decidable && (
                    <Actions data-testid="sheet-frame-actions">
                      <DecisionBtn
                        type="button"
                        disabled={blocked || busy}
                        onClick={() => handleDecide(proposal, 'valid')}
                      >
                        {confirmed ? 'Confirmed' : rejected ? 'Change to Confirmed' : 'Confirm this band'}
                      </DecisionBtn>
                      <DecisionBtn
                        type="button"
                        disabled={blocked || busy}
                        onClick={() => handleDecide(proposal, 'rejected')}
                      >
                        {rejected ? 'Rejected' : confirmed ? 'Change to Rejected' : 'Reject this band'}
                      </DecisionBtn>
                    </Actions>
                  )}

                  {conflict && conflict.fingerprint === fingerprint && (
                    <ConflictNote role="status" data-testid="sheet-frame-conflict">
                      This decision was changed somewhere else, so nothing here was overwritten.
                      {conflict.current
                        ? ` It is now “${STATE_LABELS[
                          conflict.current.verdict === 'valid' ? STATE_CONFIRMED : STATE_REJECTED
                        ]}” at revision ${conflict.current.revision}.`
                        : ' It no longer exists.'}
                      {' Review the refreshed decision before changing it again.'}
                    </ConflictNote>
                  )}
                </Row>
              );
            })}
          </List>
        )}

        {absentDecisions.length > 0 && (
          <Section data-testid="sheet-frame-absent-decisions">
            <SectionTitle>Decisions with no current proposal</SectionTitle>
            <Note>
              These bands were confirmed before, but the detector no longer proposes them, so the
              completed review is not actionable.
            </Note>
            <DiagnosticList>
              {absentDecisions.map((d) => (
                <li key={d.decisionId || shortId(d.authorizationFingerprint)}>
                  Decision {shortId(d.decisionId)}
                </li>
              ))}
            </DiagnosticList>
          </Section>
        )}

        {otherDiagnostics.length > 0 && (
          <Section data-testid="sheet-frame-diagnostics">
            <SectionTitle>Diagnostics</SectionTitle>
            <DiagnosticList>
              {otherDiagnostics.map((d) => (
                <li key={`${d.reasonCode}:${d.decisionId || ''}`}>
                  {RESOLUTION_NOTES[d.reasonCode] || d.reasonCode}
                </li>
              ))}
            </DiagnosticList>
          </Section>
        )}
      </Body>
    </PanelRoot>
  );
}
